import { setAriaHidden, setInert } from '../Utils/accessibility-utils';

export class SuccessStoriesCarousel {
    private testimonials = 0;
    private activeIndex = 0;
    private isTransitioning = false;
    private autoRotateInterval: number | null = null;
    private element: HTMLElement;
    private permanentlyStopped = false;
    private keyboardNavigating = false;

    constructor(element: HTMLElement, testimonials: number) {
        this.element = element;
        this.testimonials = testimonials;
        this.init();
    }

    private init = () => {
        const prevButton = this.element.querySelector('.success-stories-carousel__prev') as HTMLButtonElement | null;
        const nextButton = this.element.querySelector('.success-stories-carousel__next') as HTMLButtonElement | null;
        const dots = this.element.querySelectorAll('.success-stories-carousel__dot');
        const logoItems = this.element.querySelectorAll('.success-stories-carousel__logo-item');
        const cards = this.element.querySelectorAll('.success-stories-carousel__card');
        const cta = this.element.querySelector<HTMLAnchorElement>('#discoverAllStories');

        // Remove logos from keyboard/AT
        logoItems.forEach(l => {
            (l as HTMLElement).tabIndex = -1;
            l.setAttribute('aria-hidden', 'true');
        });

        // Grab all slide links
        const slideLinks: HTMLAnchorElement[] = Array.from(cards).map(card =>
            card.querySelector<HTMLAnchorElement>('.success-stories-carousel__read-more')!
        );

        const enableSlideLinks = () => {
            slideLinks.forEach((link, idx) => {
                if (idx === this.activeIndex) link.removeAttribute('tabindex');
                else link.setAttribute('tabindex', '-1');
            });
        };

        const disableSlideLinks = () => {
            slideLinks.forEach(link => link.setAttribute('tabindex', '-1'));
        };

        const focusNextAfterCarousel = () => {
            const selector = [
                'a[href]',
                'button:not([disabled])',
                'input:not([disabled]):not([type="hidden"])',
                'select:not([disabled])',
                'textarea:not([disabled])',
                '[tabindex]:not([tabindex="-1"])'
            ].join(',');

            const isVisible = (el: HTMLElement) =>
                !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length) &&
                getComputedStyle(el).visibility !== 'hidden';

            const candidates = Array.from(document.querySelectorAll<HTMLElement>(selector))
                .filter(el => !this.element.contains(el) && isVisible(el));

            const target = candidates.find(el =>
                (this.element.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING) !== 0
            );

            if (target) {
                target.focus({ preventScroll: true });
                target.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
                return;
            }

            const sentinel = document.createElement('span');
            sentinel.className = 'sr-only success-stories-carousel__exit-sentinel';
            sentinel.tabIndex = 0;
            this.element.insertAdjacentElement('afterend', sentinel);
            const cleanup = () => sentinel.remove();
            sentinel.addEventListener('blur', cleanup, { once: true });
            sentinel.focus({ preventScroll: true });
        };

        // Core slide function
        const goToSlide = (index: number, force = false) => {
            if (this.isTransitioning || (!force && index === this.activeIndex)) return;
            this.isTransitioning = true;
            this.activeIndex = index;

            const slidesContainer = this.element.querySelector('.success-stories-carousel__slides-container') as HTMLElement;
            const cardWidth = (cards[0] as HTMLElement).getBoundingClientRect().width;
            const slideGap = 16;
            slidesContainer.style.transform = `translateX(-${this.activeIndex * (cardWidth + slideGap)}px)`;

            dots.forEach((dot, idx) => {
                dot.classList.toggle('active', idx === this.activeIndex);
                dot.setAttribute('aria-label', idx === this.activeIndex ? 'Current testimonial' : `Go to testimonial ${idx + 1}`);
                if (idx === this.activeIndex) dot.setAttribute('aria-current', 'true');
                else dot.removeAttribute('aria-current');
            });
            logoItems.forEach((logo, idx) => logo.classList.toggle('active', idx === this.activeIndex));

            cards.forEach((card, idx) => {
                const isActive = idx === this.activeIndex;
                setAriaHidden(card as HTMLElement, !isActive);
                setInert(card as HTMLElement, !isActive);
            });

            enableSlideLinks(); // restore correct tab state whenever slide changes

            setTimeout(() => (this.isTransitioning = false), 500);
        };

        goToSlide(0, true); // first logo already has 'active' class

        slideLinks.forEach(link => {
            link.addEventListener('focus', () => {
                (cards[this.activeIndex] as HTMLElement)?.scrollIntoView({ block: 'start', behavior: 'smooth' });
            });
        });

        // Pointer controls
        prevButton?.addEventListener('click', () => {
            goToSlide(this.activeIndex === 0 ? this.testimonials - 1 : this.activeIndex - 1);
        });
        nextButton?.addEventListener('click', () => {
            goToSlide(this.activeIndex === this.testimonials - 1 ? 0 : this.activeIndex + 1);
        });
        dots.forEach((dot, idx) => dot.addEventListener('click', () => goToSlide(idx)));
        logoItems.forEach((logo, idx) => logo.addEventListener('click', () => goToSlide(idx)));

        // Keyboard navigation inside slides
        slideLinks.forEach((link, idx) => {
            link.addEventListener('keydown', e => {
                if (e.key !== 'Tab') return;

                const lastIndex = slideLinks.length - 1;

                // Forward from last slide
                if (!e.shiftKey && idx === lastIndex) {
                    e.preventDefault();

                    if (cta) {
                        // CTA exists: focus it and prevent looping back into slides
                        disableSlideLinks();
                        cta.removeAttribute('tabindex');
                        cta.focus({ preventScroll: true });
                        // Keep the component in view for context
                        this.element.scrollIntoView({ block: 'start', behavior: 'smooth' });
                    } else {
                        // No CTA: jump straight to the next focusable after the carousel (no extra Tab needed)
                        // Keep slide links enabled so Shift+Tab returns naturally if desired
                        focusNextAfterCarousel();
                    }
                    return;
                }

                // Normal intra-carousel navigation
                const nextIndex = e.shiftKey ? idx - 1 : idx + 1;
                if (nextIndex < 0 || nextIndex > lastIndex) {
                    // Allow natural exit (Shift+Tab from first slide)
                    return;
                }

                e.preventDefault();
                goToSlide(nextIndex);
                slideLinks[nextIndex]?.focus({ preventScroll: true });
            });
        });

        // CTA key handling
        cta?.addEventListener('keydown', e => {
            if (e.key !== 'Tab') return;

            // Shift+Tab -> re-enable slide links and go to last slide
            if (e.shiftKey) {
                e.preventDefault();
                enableSlideLinks();
                cta.setAttribute('tabindex', '-1');
                slideLinks[slideLinks.length - 1]?.focus({ preventScroll: true });
                return;
            }

            // Forward Tab -> keep slide links disabled so focus leaves component
            disableSlideLinks();
            // allow default to move focus onward
        });

        // Bring component into view whenever CTA receives focus
        cta?.addEventListener('focus', () => {
            this.element.scrollIntoView({ block: 'start', behavior: 'smooth' });
        });

        // ----- Auto-rotation (original for mouse users) -----
        const startAutoRotate = () => {
            if (this.permanentlyStopped) return;
            stopAutoRotate();
            this.autoRotateInterval = window.setInterval(() => {
                const newIndex = this.activeIndex === this.testimonials - 1 ? 0 : this.activeIndex + 1;
                goToSlide(newIndex);
            }, 5000);
        };
        const stopAutoRotate = () => {
            if (this.autoRotateInterval) {
                clearInterval(this.autoRotateInterval);
                this.autoRotateInterval = null;
            }
        };
        startAutoRotate();

        this.element.addEventListener('mouseenter', () => {
            if (!this.permanentlyStopped) stopAutoRotate();
        });
        this.element.addEventListener('mouseleave', () => {
            if (!this.permanentlyStopped) startAutoRotate();
        });

        // Detect keyboard vs mouse
        document.addEventListener('keydown', e => {
            if (e.key === 'Tab') this.keyboardNavigating = true;
        });
        document.addEventListener('mousedown', () => {
            this.keyboardNavigating = false;
        });

        // Permanently stop on keyboard focus entry
        this.element.addEventListener('focusin', () => {
            if (this.keyboardNavigating && !this.permanentlyStopped) {
                this.permanentlyStopped = true;
                stopAutoRotate();
            }
        });
    };
}

document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('.success-stories-carousel__container').forEach(element => {
        const testimonialElements = element.querySelectorAll('.success-stories-carousel__logo-item');
        new SuccessStoriesCarousel(element as HTMLElement, testimonialElements.length);
    });
});